import { useEffect, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { X, FolderKanban, ArrowLeft } from 'lucide-react'
import { projectsApi } from '@/services/api'

interface ProjectInfo {
  id: string
  name: string
  clientId: string | null
  client: { id: string; name: string } | null
}

/**
 * Reads ?projectId from URL, fetches project info, and returns filter state.
 * Returns { projectId, clientId, projectName, clientName } for the parent to use for filtering.
 */
export function useProjectFilter() {
  const [searchParams, setSearchParams] = useSearchParams()
  const projectId = searchParams.get('projectId')
  const [project, setProject] = useState<ProjectInfo | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!projectId) {
      setProject(null)
      return
    }
    setLoading(true)
    projectsApi
      .getById(projectId)
      .then((res: unknown) => {
        const resObj = res as Record<string, unknown>
        const raw = (resObj['data'] ?? res) as Record<string, unknown>
        const data = (raw['data'] ?? raw) as Record<string, unknown>

        const clientRaw = data['client'] as Record<string, unknown> | undefined

        setProject({
          id: data['id'] as string,
          name: data['name'] as string,
          clientId:
            (data['clientId'] as string | undefined) ??
            (clientRaw?.['id'] as string | undefined) ??
            null,
          client:
            clientRaw != null
              ? {
                  id: clientRaw['id'] as string,
                  name: clientRaw['name'] as string,
                }
              : null,
        })
      })
      .catch(() => setProject(null))
      .finally(() => setLoading(false))
  }, [projectId])

  const clearFilter = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('projectId')
    setSearchParams(next, { replace: true })
    setProject(null)
  }

  return {
    projectId,
    clientId: project?.clientId ?? null,
    projectName: project?.name ?? null,
    clientName: project?.client?.name ?? null,
    isFiltered: Boolean(projectId),
    isLoading: loading,
    clearFilter,
  }
}

interface ProjectFilterBannerProps {
  projectId: string | null
  projectName: string | null
  clientName?: string | null
  onClear: () => void
}

export function ProjectFilterBanner({
  projectId,
  projectName,
  clientName,
  onClear,
}: ProjectFilterBannerProps) {
  if (!projectName || !projectId) return null

  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-accent-50 border border-accent-200 rounded-lg text-sm">
      <Link
        to={`/projects/${projectId}`}
        className="flex items-center gap-1.5 text-accent-600 hover:text-accent-800 font-medium transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" />
        Torna al progetto
      </Link>
      <span className="text-accent-300 mx-1">|</span>
      <FolderKanban className="w-4 h-4 text-accent-600 flex-shrink-0" />
      <span className="text-accent-700">
        <span className="font-medium">{projectName}</span>
        {clientName && (
          <span className="text-accent-500"> ({clientName})</span>
        )}
      </span>
      <button
        onClick={onClear}
        className="ml-auto p-1 rounded text-accent-400 hover:text-accent-700 hover:bg-accent-100 transition-colors"
        title="Rimuovi filtro"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}
